import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  actionLabel,
  diffAuditValues,
  entityTypeLabel,
  formatAuditTimestamp,
} from "@/lib/auditFormat";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { Table } from "@/components/ui/Table";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Modal } from "@/components/ui/Modal";
import type { AuditLogEntry, AuditLogListResponse } from "@/types/audit";
import { ACTION_LABELS, ENTITY_TYPE_LABELS } from "@/types/audit";

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const { setPageMeta } = usePageHeader();
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [entityType, setEntityType] = useState("");
  const [actorLogin, setActorLogin] = useState("");
  const [action, setAction] = useState("");

  const [selected, setSelected] = useState<AuditLogEntry | null>(null);

  const entityTypeOptions = useMemo(
    () => Object.entries(ENTITY_TYPE_LABELS).sort((a, b) => a[1].localeCompare(b[1], "de")),
    [],
  );
  const actionOptions = useMemo(
    () => Object.entries(ACTION_LABELS).sort((a, b) => a[1].localeCompare(b[1], "de")),
    [],
  );

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    if (entityType) params.set("entity_type", entityType);
    if (actorLogin.trim()) params.set("actor_login", actorLogin.trim().toLowerCase());
    if (action) params.set("action", action);

    api
      .get<AuditLogListResponse>(`/api/v1/audit?${params}`)
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [offset, entityType, actorLogin, action]);

  useEffect(() => {
    setPageMeta({
      title: "Änderungsprotokoll",
      subtitle: "Wer hat wann was geändert oder angelegt",
    });
    return () => setPageMeta(null);
  }, [setPageMeta]);

  useEffect(() => {
    load();
  }, [load]);

  function onFilterSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (offset !== 0) {
      setOffset(0);
      return;
    }
    load();
  }

  const selectedDiffs = selected
    ? diffAuditValues(selected.old_values, selected.new_values)
    : [];

  return (
    <div className="space-y-6">
      <form
        onSubmit={onFilterSubmit}
        className="grid gap-3 rounded-xl border border-separator bg-surface-secondary/30 p-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Objekttyp</span>
          <select
            className="eh-input w-full"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
          >
            <option value="">Alle</option>
            {entityTypeOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Aktion</span>
          <select
            className="eh-input w-full"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          >
            <option value="">Alle</option>
            {actionOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="mb-1 block text-label-secondary">Benutzer (Login)</span>
          <input
            className="eh-input w-full"
            value={actorLogin}
            onChange={(e) => setActorLogin(e.target.value)}
            placeholder="z. B. max.mustermann"
          />
        </label>
        <div className="flex items-end sm:col-span-2 lg:col-span-4">
          <Button type="submit" disabled={loading}>
            Filtern
          </Button>
        </div>
      </form>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Lade Protokoll…</p>
      ) : (
        <>
          <p className="text-sm text-label-secondary">
            {total} Eintrag{total === 1 ? "" : "e"}
            {total > PAGE_SIZE
              ? ` (Seite ${Math.floor(offset / PAGE_SIZE) + 1} von ${Math.ceil(total / PAGE_SIZE)})`
              : ""}
          </p>
          <Table wide emptyMessage="Keine Einträge für die Filter." isEmpty={items.length === 0}>
            <thead>
              <tr>
                <th>Zeitpunkt</th>
                <th>Benutzer</th>
                <th>Objekt</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {items.map((entry) => (
                <ClickableTableRow
                  key={entry.id}
                  ariaLabel="Protokolleintrag anzeigen"
                  onActivate={() => setSelected(entry)}
                >
                  <td>{formatAuditTimestamp(entry.created_at)}</td>
                  <td>{entry.actor_login ?? "—"}</td>
                  <td>
                    {entityTypeLabel(entry.entity_type)}
                    <span className="block font-mono text-xs text-label-secondary">
                      {entry.entity_id}
                    </span>
                  </td>
                  <td>{actionLabel(entry.action)}</td>
                </ClickableTableRow>
              ))}
            </tbody>
          </Table>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={offset === 0 || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              Zurück
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={offset + PAGE_SIZE >= total || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Weiter
            </Button>
          </div>
        </>
      )}

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? actionLabel(selected.action) : ""}
        description={
          selected
            ? `${entityTypeLabel(selected.entity_type)} · ${formatAuditTimestamp(selected.created_at)} · ${selected.actor_login ?? "unbekannt"}`
            : undefined
        }
        size="lg"
      >
        {selected ? (
          <div className="space-y-4">
            {selectedDiffs.length === 0 ? (
              <p className="text-sm text-label-secondary">
                Keine Feldunterschiede — ggf. wurde der Datensatz neu angelegt.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-label-secondary">
                    <th className="py-2 pr-3 text-left">Feld</th>
                    <th className="py-2 pr-3 text-left">Vorher</th>
                    <th className="py-2 text-left">Nachher</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDiffs.map((row) => (
                    <tr key={row.field} className="border-t border-separator">
                      <td className="py-2 pr-3 font-mono text-xs">{row.field}</td>
                      <td className="max-w-[35%] py-2 pr-3 whitespace-pre-wrap break-words text-status-red/90">
                        {row.before}
                      </td>
                      <td className="max-w-[35%] py-2 whitespace-pre-wrap break-words text-status-green">
                        {row.after}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
